import React, { useState, useEffect } from 'react';
import config from '../config';
import Layout from '../components/Layout';
import './CPSessionInfo.css';

const CPSessionInfo = () => {
  const [cp, setCp] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filteredCp, setFilteredCp] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  // Update filtered data when cp data changes
  useEffect(() => {
    setFilteredCp(cp);
    setCurrentPage(1); // Reset to first page when data changes
  }, [cp]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch cp and programs in parallel
      const [cpResponse, programsResponse] = await Promise.all([
        fetch(`${config.api.baseUrl}/cycle-planning/cp_session`, {
          credentials: 'include'
        }),
        fetch(`${config.api.baseUrl}/cycle-planning/programs`, {
          credentials: 'include'
        })
      ]);

      if (cpResponse.ok && programsResponse.ok) {
        const cpData = await cpResponse.json();
        const programsData = await programsResponse.json();
        
        setCp(cpData.projects || []);
        setPrograms(programsData.programs || []);
      } else {
        console.error('Failed to fetch data');
        setMessage('Failed to load data');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setMessage('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const handleCellClick = (cpId, field, currentValue) => {
    setEditingCell({ cpId: cpId, field });
    setEditValue(currentValue || '');
  };

  const handleCellBlur = () => {
    if (!saving) {
      setEditingCell(null);
      setEditValue('');
    }
  };

  const handleCellKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCellBlur();
    }
  };

  const handleSave = async (valueToSave = null) => {
    if (!editingCell) return;

    const { cpId, field } = editingCell;
    const value = valueToSave !== null ? valueToSave : editValue;
    setSaving(true);

    try {
      const response = await fetch(`${config.api.baseUrl}/cycle-planning/sessions/${cpId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          field: field,
          value: value
        })
      });

      if (response.ok) {
        // Update local state
        setCp(prev => prev.map(cp => 
          cp.id === cpId 
            ? { ...cp, [field]: value }
            : cp
        ));
        setMessage('Updated successfully');
        setTimeout(() => setMessage(''), 3000);
        
        // Close editing state after successful save
        setEditingCell(null);
        setEditValue('');
      } else {
        const errorData = await response.json();
        setMessage(`Error: ${errorData.error || 'Failed to update'}`);
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      console.error('Error updating session:', error);
      setMessage('Error updating session');
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  const getProgramName = (programId) => {
    const program = programs.find(p => p.id === programId);
    return program ? program.program_name : 'No Program';
  };

  // Pagination functions
  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredCp.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(filteredCp.length / itemsPerPage);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  if (loading) {
    return (
      <Layout>
        <div className="cp-management">
          <div className="loading">Loading CP data...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="cp-management">
      <div className="header">
        <h1>CP Master Data</h1>
        <p>Click on any cell to edit. Press Enter to save, Escape to cancel.</p>
        {message && (
          <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}
      </div>

      <div className="table-container">
        <table className="cp-table">
          <thead>
            <tr>
              <th>Session Name</th>
              <th>Program</th>
              <th>AM</th>
              <th>PM</th>
            </tr>
          </thead>
          <tbody>
            {getCurrentPageData().map(cp => (
              <tr key={cp.id}>
                <td>
                  {editingCell?.cpId === cp.id && editingCell?.field === 'cycle_planning_name' ? (
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={handleCellBlur}
                      onKeyDown={handleCellKeyPress}
                      autoFocus
                      disabled={saving}
                    />
                  ) : (
                    <span 
                      className="editable-cell"
                      onClick={() => handleCellClick(cp.id, 'cycle_planning_name', cp.cycle_planning_name)}
                    >
                      {cp.cycle_planning_name}
                    </span>
                  )}
                </td>
                <td>
                  {editingCell?.cpId === cp.id && editingCell?.field === 'program_id' ? (
                    <select
                      value={editValue}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setEditValue(newValue);
                        // Auto-save when dropdown value changes
                        handleSave(newValue);
                      }}
                      onKeyDown={handleCellKeyPress}
                      autoFocus
                      disabled={saving}
                    >
                      <option value="">No Program</option>
                      {programs.map(program => (
                        <option key={program.id} value={program.id}>
                          {program.program_name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span 
                      className="editable-cell"
                      onClick={() => handleCellClick(cp.id, 'program_id', cp.program_id)}
                    >
                      {getProgramName(cp.program_id)}
                    </span>
                  )}
                </td>
                <td>
                  {editingCell?.cpId === cp.id && editingCell?.field === 'am' ? (
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={handleCellBlur}
                      onKeyDown={handleCellKeyPress}
                      autoFocus
                      disabled={saving}
                    />
                  ) : (
                    <span 
                      className="editable-cell"
                      onClick={() => handleCellClick(cp.id, 'am', cp.am)}
                    >
                      {cp.am || 'Not set'}
                    </span>
                  )}
                </td>
                <td>
                  {editingCell?.cpId === cp.id && editingCell?.field === 'pm' ? (
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={handleCellBlur}
                      onKeyDown={handleCellKeyPress}
                      autoFocus
                      disabled={saving}
                    />
                  ) : (
                    <span 
                      className="editable-cell"
                      onClick={() => handleCellClick(cp.id, 'pm', cp.pm)}
                    >
                      {cp.pm || 'Not set'}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="pagination-controls">
        <div className="pagination-info">
          <span>
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredCp.length)} of {filteredCp.length} entries
          </span>
          <div className="items-per-page">
            <label htmlFor="itemsPerPage">Items per page:</label>
            <select 
              id="itemsPerPage"
              value={itemsPerPage} 
              onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
        
        <div className="pagination-buttons">
          <button 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          
          <div className="page-numbers">
            {Array.from({ length: getTotalPages() }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
              >
                {page}
              </button>
            ))}
          </div>
          
          <button 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === getTotalPages()}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      </div>

      <div className="actions">
        <button onClick={fetchData} className="refresh-btn">
          Refresh Data
        </button>
      </div>
      </div>
    </Layout>
  );
};

export default CPSessionInfo;
